import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtISODate } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import { Plus } from "lucide-react";

export default function LeaveRequests() {
  const { user } = useAuth();
  const isMgmt = ["registered_manager", "deputy_manager", "compliance_manager", "nominated_individual", "administrator", "super_admin"].includes(user.role);
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    worker_id: "", start_date: fmtISODate(new Date()),
    end_date: fmtISODate(new Date()), leave_type: "annual", reason: "",
  });
  const [decisionNote, setDecisionNote] = useState({});

  const load = () => api.get("/leave-requests").then(({ data }) => setItems(data.items || []));
  useEffect(() => {
    load();
    if (isMgmt) api.get("/staff").then(({ data }) => setStaff(data.items || [])).catch(() => {});
  }, [isMgmt]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    if (!isMgmt) delete payload.worker_id;
    await api.post("/leave-requests", payload);
    setCreating(false);
    setForm({ ...form, reason: "" });
    load();
  };

  const decide = async (id, action) => {
    await api.put(`/leave-requests/${id}/decide`, { action, note: decisionNote[id] || "" });
    setDecisionNote({ ...decisionNote, [id]: "" });
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Leave &amp; availability</h1>
          <p className="text-sm text-slate-600">Annual leave, sickness, training and compassionate leave requests.</p>
        </div>
        <button onClick={() => setCreating((v) => !v)} data-testid="new-leave-button" className="inline-flex items-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white px-4 py-2 rounded-md font-medium min-h-[44px]">
          <Plus className="h-4 w-4" /> New request
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="leave-form">
          {isMgmt && (
            <select value={form.worker_id} onChange={(e) => setForm({ ...form, worker_id: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md">
              <option value="">My request</option>
              {staff.map((s) => <option key={s.id} value={s.id}>For: {s.full_name}</option>)}
            </select>
          )}
          <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" data-testid="leave-type">
            {["annual", "sick", "training", "compassionate", "unpaid", "other"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md" />
          <textarea placeholder="Reason / notes" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} className="md:col-span-2 px-3 py-2 border border-slate-300 rounded-md" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border border-slate-300 rounded-md">Cancel</button>
            <button type="submit" data-testid="leave-submit" className="px-4 py-2 bg-[#0D7377] text-white rounded-md font-medium">Submit request</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200"><th className="text-left py-2 px-3">Worker</th><th className="text-left py-2 px-3">Type</th><th className="text-left py-2 px-3">Dates</th><th className="text-left py-2 px-3">Status</th><th></th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-3 px-3 text-slate-800">{r.worker_name}</td>
                <td className="py-3 px-3 text-slate-700 capitalize">{r.leave_type}</td>
                <td className="py-3 px-3 text-slate-700">{r.start_date} → {r.end_date}</td>
                <td className="py-3 px-3"><StatusBadge status={r.status === "approved" ? "active" : r.status === "declined" ? "overdue" : "pending"} label={r.status} /></td>
                <td className="py-3 px-3 text-right">
                  {isMgmt && r.status === "pending" && (
                    <div className="flex items-center gap-1 justify-end">
                      <input placeholder="Note" value={decisionNote[r.id] || ""} onChange={(e) => setDecisionNote({ ...decisionNote, [r.id]: e.target.value })} className="text-xs px-2 py-1 border border-slate-300 rounded w-28" />
                      <button onClick={() => decide(r.id, "approve")} data-testid={`leave-approve-${r.id}`} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded">Approve</button>
                      <button onClick={() => decide(r.id, "decline")} data-testid={`leave-decline-${r.id}`} className="text-xs px-2 py-1 bg-red-600 text-white rounded">Decline</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-500">No leave requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
